'use client';

import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { useEnvironmentStore } from '@/lib/environments/store';
import type { EnvironmentSecret } from '@/lib/environments/types';
import {
  Plus,
  Trash2,
  Eye,
  EyeOff,
  Key,
  Clock,
  Activity,
  AlertTriangle,
} from 'lucide-react';

interface SecretsManagerProps {
  environmentId: string;
}

export function SecretsManager({ environmentId }: SecretsManagerProps) {
  const { secrets, secretsLoading, loadSecrets, setSecret, deleteSecret } =
    useEnvironmentStore();

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [showValue, setShowValue] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSecrets(environmentId);
  }, [environmentId, loadSecrets]);

  const handleAdd = async () => {
    if (!newKey.trim() || !newValue.trim()) {
      setError('Key and value are required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await setSecret(environmentId, newKey, newValue, newDescription || undefined);
      setAddDialogOpen(false);
      setNewKey('');
      setNewValue('');
      setNewDescription('');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (key: string) => {
    if (confirm(`Are you sure you want to delete the secret "${key}"? This cannot be undone.`)) {
      try {
        await deleteSecret(environmentId, key);
      } catch (err) {
        console.error('Failed to delete secret:', err);
      }
    }
  };

  if (secretsLoading && secrets.length === 0) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium flex items-center gap-2">
            <Key className="size-4" />
            Environment Secrets
          </h3>
          <p className="text-sm text-muted-foreground">
            Securely store sensitive values for this environment
          </p>
        </div>
        <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="size-4 mr-2" />
              Add Secret
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Secret</DialogTitle>
              <DialogDescription>
                Add a new secret to this environment. Values are encrypted at rest.
              </DialogDescription>
            </DialogHeader>

            {error && (
              <Alert variant="destructive">
                <AlertTriangle className="size-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="secret-key">Key</Label>
                <Input
                  id="secret-key"
                  value={newKey}
                  onChange={(e) => setNewKey(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_'))}
                  placeholder="DATABASE_URL"
                  className="mt-1 font-mono"
                />
                <p className="text-sm text-muted-foreground mt-1">
                  Uppercase letters, numbers, and underscores only
                </p>
              </div>

              <div>
                <Label htmlFor="secret-value">Value</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    id="secret-value"
                    type={showValue ? 'text' : 'password'}
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    placeholder="Enter secret value"
                    className="font-mono"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowValue(!showValue)}
                  >
                    {showValue ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                  </Button>
                </div>
              </div>

              <div>
                <Label htmlFor="secret-description">Description (optional)</Label>
                <Textarea
                  id="secret-description"
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  placeholder="What is this secret used for?"
                  className="mt-1"
                  rows={2}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleAdd} disabled={saving}>
                {saving ? 'Adding...' : 'Add Secret'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {secrets.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground border rounded-lg">
          <Key className="size-12 mx-auto mb-4 opacity-50" />
          <p>No secrets configured</p>
          <p className="text-sm">Add secrets to store sensitive values</p>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Key</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="text-center" title="Last Rotated">
                <Clock className="size-4 inline" />
              </TableHead>
              <TableHead className="text-center" title="Access Count">
                <Activity className="size-4 inline" />
              </TableHead>
              <TableHead className="w-[80px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {secrets.map((secret) => (
              <TableRow key={secret.key}>
                <TableCell className="font-mono text-sm">{secret.key}</TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  {secret.description || '-'}
                </TableCell>
                <TableCell className="text-center text-sm text-muted-foreground">
                  {secret.last_rotated_at
                    ? new Date(secret.last_rotated_at).toLocaleDateString()
                    : 'Never'}
                </TableCell>
                <TableCell className="text-center text-sm text-muted-foreground">
                  {secret.access_count}
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(secret.key)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <p className="text-xs text-muted-foreground">
        Secret values are encrypted at rest. Only the key names are displayed.
        To update a secret, delete and re-add it with the new value.
      </p>
    </div>
  );
}
